import type { FC } from "react";
import { toast } from "react-toastify";
import { Copy01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import type { OwnerReportShare } from "api/owner-report-shares";
import { shareMetaLine, STATE_LABELS, STATE_VARIANTS } from "./helpers";

/**
 * Every link this application has been shared on, newest first. A row is a share
 * rather than an owner: one link, one state, one view count and one Revoke
 * across every address on it.
 */
export const SharedLinksTab: FC<{
  shares: OwnerReportShare[];
  failed: boolean;
  revokingId: number | null;
  onRevoke: (share: OwnerReportShare) => void;
}> = ({ shares, failed, revokingId, onRevoke }) => {
  const copy = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Could not copy. Select the link and copy it."));
  };

  // A failed fetch must not read as "never shared" — that would hide live links the partner
  // may have opened this tab specifically to revoke.
  if (failed)
    return (
      <p className="py-10 text-center text-sm text-content-500">
        These links could not be loaded. Please close this and try again.
      </p>
    );

  if (shares.length === 0)
    return (
      <p className="py-10 text-center text-sm text-content-400">
        This application has not been shared with an owner yet.
      </p>
    );

  return (
    <div className="flex flex-col gap-2 py-2">
      {shares.map((share) => (
        <div
          key={share.id}
          className="flex items-start justify-between gap-4 rounded-lg border border-alpha-40 px-4 py-3"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {share.emails.map((email) => (
                <span
                  key={email}
                  className="truncate text-sm font-medium text-content-1000"
                >
                  {email}
                </span>
              ))}
            </div>
            <span className="text-xs text-content-400">
              {shareMetaLine(share)}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Badge variant={STATE_VARIANTS[share.state]} size={BadgeSize.SM}>
              {STATE_LABELS[share.state]}
            </Badge>

            <Show if={share.state === "active" && Boolean(share.url)}>
              <IconButton
                HugeIcon={Copy01Icon}
                variant={IconButtonVariant.TEXT_BASE}
                size={IconButtonSize.SM}
                onClick={() => copy(share.url ?? "")}
                aria-label="Copy link"
              />
            </Show>

            <Show if={share.state === "active"}>
              <Button
                variant={ButtonVariant.DANGER}
                size={ButtonSize.SM}
                onClick={() => onRevoke(share)}
                loading={revokingId === share.id}
                disabled={revokingId !== null}
              >
                Revoke
              </Button>
            </Show>
          </div>
        </div>
      ))}
    </div>
  );
};
